import { formatCurrency } from './currency';
import { t } from '../i18n';

// Rank, headline, and signed-net display for the end-game recap.
// calculateSettlements already has the nets; this file only decides who
// the screen treats as the winner when buy-ins differ.

/**
 * Signed chip amounts for nets. formatCurrency itself is unsigned
 * (`$-1.00` for a negative), so losses go through here instead.
 * Minus sign is U+2212 so it doesn't look like a stray hyphen on `$`.
 */
export function formatSignedCurrency(cents, currency, locale) {
  const n = Math.round(Number(cents) || 0);
  if (n < 0) return `−${formatCurrency(-n, currency, locale)}`;
  if (n > 0) return `+${formatCurrency(n, currency, locale)}`;
  return formatCurrency(0, currency, locale);
}

export function compareSettlementRank(a, b) {
  const netA = a.netResult || 0;
  const netB = b.netResult || 0;
  if (netB !== netA) return netB - netA;
  const stackA = a.finalChips ?? a.stack ?? 0;
  const stackB = b.finalChips ?? b.stack ?? 0;
  if (stackB !== stackA) return stackB - stackA;
  return String(a.name || '').localeCompare(String(b.name || ''));
}

export function rankSettlementPlayers(players) {
  return [...(players || [])].sort(compareSettlementRank);
}

/**
 * Who gets the headline / WINNER badge.
 * Rank by net, then stack. All-even and nobody-up skip the badge
 * and don't claim someone "left 'em chipless."
 */
export function settlementRecap(players) {
  const ranked = rankSettlementPlayers(players);
  const top = ranked[0] || null;
  const topNet = top ? (top.netResult || 0) : 0;
  const allEven = ranked.length > 0 && ranked.every((p) => (p.netResult || 0) === 0);
  const nobodyUp = !top || topNet <= 0;

  if (!top || allEven || nobodyUp) {
    return {
      ranked,
      winner: null,
      winnerId: null,
      allEven: true,
      headlineName: null,
      headlineRest: [t('settle.brokeEven0'), t('settle.brokeEven1')],
    };
  }

  return {
    ranked,
    winner: top,
    winnerId: top.id,
    allEven: false,
    headlineName: top.name,
    headlineRest: [t('settle.leftEm0'), t('settle.leftEm1')],
  };
}
